import {
	Box,
	Button,
	CardActions,
	CardContent,
	FormHelperText,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { useFileUploader } from '@/hooks/useFileUploader';
import { ExerciseTagDTO, FileInfo } from '@/api/DTO/common.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import dayjs from 'dayjs';
import { GameBlessingCardTreasureDTO } from '@/api/DTO/treasury/wish.interface';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import {
	useCreateWishTreasureMutation,
	useUpdateWishTreasureDetailMutation,
} from '@/api/treasury/wishApiSlice';
import { QualityType } from '@/api/enum/common.enum';

interface Props {
	type: FormType;
	details?: GameBlessingCardTreasureDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function SingleInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createWishTreasure] = useCreateWishTreasureMutation();
	const [updateWishTreasureDetail] = useUpdateWishTreasureDetailMutation();
	const { uploadFile } = useFileUploader();
	const [imageFile, setImageFile] = useState<FileInfo | null>(null);
	const [imageFile2, setImageFile2] = useState<FileInfo | null>(null);
	const { wishId, wishSingleId } = useParams();
	const imageInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef2 = useRef<HTMLInputElement>(null);

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const defaultValues: DefaultValues<GameBlessingCardTreasureDTO> = {};
	useEffect(() => {
		// console.log(details);
		if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
		if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
		if (details?.publishedStartAt)
			methods.setValue('publishedStartAt', details.publishedStartAt);
		if (details?.publishedEndAt)
			methods.setValue('publishedEndAt', details.publishedEndAt);
		if (details?.costPoints) methods.setValue('costPoints', details.costPoints);
		if (Number(details?.quality) > -1)
			methods.setValue('quality', details?.quality);

		if (details?.treasurePic) {
			setImageFile(details.treasurePic);
			methods.setValue('cover', 'file');
		}
		if (details?.treasurePositionPic) {
			setImageFile2(details.treasurePositionPic);
			methods.setValue('cover2', 'file');
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		costPoints: Yup.string().required(t('errorMessage.required')),
		quality: Yup.string().required(t('errorMessage.required')),

		cover: Yup.string().required(t('errorMessage.pleaseSelect')),
		cover2: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<GameBlessingCardTreasureDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const coverField = useController({
		name: 'cover',
		control: methods.control,
	});
	const coverField2 = useController({
		name: 'cover2',
		control: methods.control,
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<GameBlessingCardTreasureDTO> = async (data) => {
		const response =
			type == FormType.create
				? await createWishTreasure({
						...data,
						treasurePic: imageFile,
						wishId: wishId,
						treasurePositionPic: imageFile2,
				  })
				: await updateWishTreasureDetail({
						...data,
						treasurePic: imageFile,
						id: wishSingleId || '',
						seriesId: wishId,
						treasurePositionPic: imageFile2,
				  });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.addTreasureSuccess')
					: t('snackbar.changeConentSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/treasury-manage/wish-list/info/${wishId}`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/treasury-manage/wish-list/info/${wishId}`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			if (type == 'cover') {
				setImageFile(res);
				methods.setValue('cover', res.name);

				methods.trigger('cover');
			}
			if (type == 'cover2') {
				setImageFile2(res);
				methods.setValue('cover2', res.name);

				methods.trigger('cover2');
			}
		}
	};

	const handleDeleteFile = (type: string) => {
		if (type == 'cover') {
			setImageFile(null);
			methods.setValue('cover', '');
			imageInputRef.current && (imageInputRef.current.value = '');
		}
		if (type == 'cover2') {
			setImageFile2(null);
			methods.setValue('cover2', '');
			imageInputRef2.current && (imageInputRef2.current.value = '');
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('chartletList.treasureTitle')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('chartletList.treasureTitle')}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name={'quality'}
									label={t('chartletList.quality')}
									options={qualityList}
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='costPoints'
									label={t('chartletList.integral')}
									inputType={InputType.number}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('chartletList.treasureImage')}
								</Typography>
								{imageFile && (
									<img
										src={imageFile?.url || ''}
										width={245}
										height={245}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{imageFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'cover');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'cover');
												}}
											/>
										</Button>
									)}
									{imageFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('cover');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('chartletList.treasureAllImage')}
								</Typography>
								{imageFile2 && (
									<img
										src={imageFile2?.url || ''}
										width={245}
										height={245}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{imageFile2 ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'cover2');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'cover2');
												}}
											/>
										</Button>
									)}
									{imageFile2 && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('cover2');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverField2.fieldState.error?.message}
								</FormHelperText>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
